import { useState, type ReactNode } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { Link, useLocation } from 'wouter';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { format, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import type { DateRange } from 'react-day-picker';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import {
  Calendar as CalendarIcon,
  AlertCircle,
  AlertTriangle,
  CircleCheck,
  Sparkles,
  Lightbulb,
  Info,
  ChevronDown,
  Receipt,
  Banknote,
  FileSpreadsheet,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { api } from '@/lib/api';
import { AppShell } from '@/components/app-shell';
import { useTenantMe } from '@/hooks/use-tenant-me';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart';

const ventasChartConfig = {
  ingresos: { label: 'Ingresos', color: 'hsl(var(--chart-2))' },
  egresos: { label: 'Egresos', color: 'hsl(var(--chart-1))' },
} satisfies ChartConfig;

interface DashboardData {
  period: { year: number; month: number };
  iva: {
    invoiceCount: number;
    purchaseTotal: number;
    saleTotal: number;
    ivaCredit: number;
    ivaDebit: number;
    netToPay: number;
    saldoAFavor: number;
    saldoFavorOficial: number | null; // saldo a favor acumulado oficial (SET)
    netToPayEfectivo: number; // neto del período menos el saldo oficial
  };
  nextDeclaration: {
    id: string;
    kind: string;
    formCode: string;
    dueDate: string;
    netToPay: string;
    status: string;
  } | null;
  nextVencimiento: {
    dueDate: string;
    day: number;
    dv: number;
    formCode: string;
    kind: string;
  } | null;
  yearOverview: {
    year: number;
    monthly: Array<{ label: string; purchase: number; sale: number }>;
    purchaseTotal: number;
    saleTotal: number;
    netIrpEstimate: number;
  };
  receivables: { totalPendiente: number; count: number };
  cobrosMes: { count: number; monto: number };
  cobrosRecientes: Array<{
    id: string;
    description: string;
    clientName: string;
    amount: number;
    status: string;
    createdAt: string;
  }>;
  quotesAbiertas: {
    count: number;
    items: Array<{
      id: string;
      quoteNumber: string;
      clientName: string;
      grandTotal: number;
      status: string;
    }>;
  };
}

const COBRO_STATUS: Record<string, string> = {
  pending: 'Pendiente',
  paid: 'Pagado',
  expired: 'Vencido',
  cancelled: 'Cancelado',
};
const QUOTE_STATUS: Record<string, string> = {
  draft: 'Borrador',
  sent: 'Enviado',
  accepted: 'Aceptado',
};

function formatGs(n: number): string {
  return `Gs ${Math.round(n).toLocaleString('es-PY')}`;
}

export function AppDashboardPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useTenantMe();

  // El rango controla el gráfico "Ingresos vs Egresos" (server-side). Default
  // = este mes → el chart se muestra POR DÍA (≤31 días); rangos > 1 mes, por mes.
  const [range, setRange] = useState<DateRange | undefined>({
    from: startOfMonth(new Date()),
    to: new Date(),
  });
  const from = range?.from ? format(range.from, 'yyyy-MM-dd') : '';
  const to = range?.to ? format(range.to, 'yyyy-MM-dd') : '';

  const { data, isLoading } = useQuery({
    queryKey: ['app-dashboard', from, to],
    queryFn: () => {
      const qs = from && to ? `?from=${from}&to=${to}` : '';
      return api<DashboardData>(`/api/app/dashboard${qs}`);
    },
    enabled: !!me,
    placeholderData: keepPreviousData,
  });

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/app/login');
    return null;
  }

  return (
    <AppShell>
      <div>
        <h1 className="text-2xl font-bold">Hola {me.tenant.displayName}</h1>
        <p className="text-sm text-muted-foreground">
          Resumen de tu actividad — {format(new Date(), 'MMMM yyyy', { locale: es })}
        </p>
      </div>

      <InsightsSection enabled={!!me} />

      {/* KPIs: Ingresos · Egresos · Total a pagar · Próximo vencimiento.
          Full-width en mobile (cifras en Gs de 6+ dígitos no entran al 50%). */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          icon={<ArrowUpRight className="size-4 text-success" />}
          label="Ingresos"
          value={data ? formatGs(data.iva.saleTotal) : '—'}
          hint="este mes"
          href="/app/facturas?tab=ingresos"
          loading={isLoading || !data}
        />
        <KpiCard
          icon={<ArrowDownRight className="size-4 text-muted-foreground" />}
          label="Egresos"
          value={data ? formatGs(data.iva.purchaseTotal) : '—'}
          hint="este mes"
          href="/app/facturas?tab=egresos"
          loading={isLoading || !data}
        />
        <KpiCard
          icon={<Receipt className="size-4" />}
          label="Total a pagar"
          value={data ? formatGs(data.iva.netToPay) : '—'}
          hint="estimado del mes"
          href="/app/iva"
          loading={isLoading || !data}
        />
        <KpiCard
          icon={<CalendarIcon className="size-4" />}
          label="Próximo vencimiento"
          value={
            data?.nextVencimiento
              ? format(new Date(data.nextVencimiento.dueDate + 'T12:00:00'), 'dd MMM', { locale: es })
              : '—'
          }
          hint={data?.nextVencimiento ? `IVA · ${data.nextVencimiento.formCode}` : 'sin vencimientos'}
          href="/app/iva"
          loading={isLoading || !data}
        />
      </div>

      {/* Ingresos vs Egresos */}
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="text-base">Ingresos vs Egresos</CardTitle>
            <CardDescription>Comprobantes validados por mes</CardDescription>
          </div>
          <DateRangePicker
            value={range}
            onChange={setRange}
            placeholder="Rango de fechas"
            align="end"
            disabled={{ after: new Date() }}
          />
        </CardHeader>
        <CardContent>
          {data && (
            <ChartContainer config={ventasChartConfig} className="aspect-auto h-[260px] w-full">
              <LineChart
                data={data.yearOverview.monthly.map((m) => ({
                  month: m.label,
                  ingresos: m.sale,
                  egresos: m.purchase,
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis
                  tickFormatter={(v) =>
                    v >= 1_000_000 ? `${(v / 1_000_000).toFixed(0)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
                  }
                  tick={{ fontSize: 12 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value, name, item) => (
                        <div className="flex flex-1 items-center gap-2 leading-none">
                          <span
                            className="size-2 shrink-0 rounded-[2px]"
                            style={{ background: item?.color }}
                          />
                          <span className="text-muted-foreground">
                            {name === 'ingresos' ? 'Ingresos' : 'Egresos'}
                          </span>
                          <span className="ml-auto font-mono font-medium tabular-nums">
                            {formatGs(Number(value))}
                          </span>
                        </div>
                      )}
                    />
                  }
                />
                <ChartLegend content={<ChartLegendContent />} />
                <Line type="monotone" dataKey="ingresos" stroke="var(--color-ingresos)" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="egresos" stroke="var(--color-egresos)" strokeWidth={2} dot={false} />
              </LineChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      {/* Últimos cobros | Últimos presupuestos */}
      <div className="grid gap-4 md:grid-cols-2">
        <ListCard
          title="Últimos cobros"
          icon={<Banknote className="size-4" />}
          href="/app/cobros"
          empty="Todavía no generaste cobros."
          loading={isLoading || !data}
          rows={
            data?.cobrosRecientes.map((c) => ({
              key: c.id,
              primary: c.clientName || c.description,
              secondary: COBRO_STATUS[c.status] ?? c.status,
              value: formatGs(c.amount),
            })) ?? []
          }
        />
        <ListCard
          title="Últimos presupuestos"
          icon={<FileSpreadsheet className="size-4" />}
          href="/app/presupuestos"
          empty="No tenés presupuestos abiertos."
          loading={isLoading || !data}
          rows={
            data?.quotesAbiertas.items.map((q) => ({
              key: q.id,
              primary: q.clientName,
              secondary: `${q.quoteNumber} · ${QUOTE_STATUS[q.status] ?? q.status}`,
              value: formatGs(q.grandTotal),
            })) ?? []
          }
        />
      </div>

      {/* Status onboarding banner */}
      {me.tenant.status === 'onboarding' && (
        <Card className="border-info/40 bg-info/10">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="size-4" />
              Tu cuenta está en onboarding
            </CardTitle>
            <CardDescription>
              Tu contador {me.accountant?.fullName ?? 'asignado'} está terminando de configurar tu
              cuenta. Mientras tanto, podés ir mandando facturas por WhatsApp.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {me.tenant.status === 'active' && me.accountant && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CircleCheck className="size-4 text-success" />
              Tu contador
            </CardTitle>
            <CardDescription>{me.accountant.fullName} · {me.accountant.email}</CardDescription>
          </CardHeader>
        </Card>
      )}
    </AppShell>
  );
}

function KpiCard({
  icon,
  label,
  value,
  hint,
  href,
  loading,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  hint?: string;
  href?: string;
  loading?: boolean;
}) {
  const inner = (
    <Card className="h-full gap-0 py-0 transition-colors hover:border-primary/40">
      <div className="p-4">
        <div className="mb-1 flex items-center gap-1.5 text-sm text-muted-foreground">
          {icon}
          {label}
        </div>
        {loading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <>
            <div className="text-2xl font-bold tabular-nums">{value}</div>
            {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
          </>
        )}
      </div>
    </Card>
  );
  return href ? (
    <Link href={href} className="block">
      {inner}
    </Link>
  ) : (
    inner
  );
}

interface ListRow {
  key: string;
  primary: string;
  secondary: string;
  value: string;
}

function ListCard({
  title,
  icon,
  href,
  rows,
  empty,
  loading,
}: {
  title: string;
  icon: ReactNode;
  href: string;
  rows: ListRow[];
  empty: string;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {icon}
            {title}
          </CardTitle>
          <Link
            href={href}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            Ver todo
            <ArrowRight className="size-3" />
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : rows.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">{empty}</p>
        ) : (
          <ul className="space-y-1.5">
            {rows.map((r) => (
              <li
                key={r.key}
                className="flex items-center justify-between gap-3 rounded-md border border-border bg-card px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">{r.primary}</div>
                  <div className="truncate text-xs text-muted-foreground">{r.secondary}</div>
                </div>
                <span className="shrink-0 font-mono tabular-nums">{r.value}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

interface InsightItem {
  title: string;
  body: string;
  severity: 'info' | 'opportunity' | 'warning';
}
interface InsightsData {
  hasData: boolean;
  state: 'ok' | 'processing' | 'empty';
  summary: string;
  insights: InsightItem[];
  disclaimer: string;
  generatedAt: string;
}

function severityIcon(s: InsightItem['severity']) {
  if (s === 'warning') return <AlertTriangle className="size-4 text-warning" />;
  if (s === 'opportunity') return <Lightbulb className="size-4 text-primary" />;
  return <Info className="size-4 text-muted-foreground" />;
}

const INSIGHTS_OPEN_KEY = 'actuo-insights-open';

function InsightsSection({ enabled }: { enabled: boolean }) {
  // Inicia COLAPSADO y persiste el estado entre navegaciones (localStorage).
  const [open, setOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(INSIGHTS_OPEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setOpen((v) => {
      const next = !v;
      try {
        localStorage.setItem(INSIGHTS_OPEN_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });

  // LAZY: la generación (LLM) recién se dispara al abrir; luego cachea (1 día
  // en cliente + en server por tenant/día).
  const { data, isLoading } = useQuery({
    queryKey: ['app-insights'],
    queryFn: () => api<InsightsData>('/api/app/insights'),
    enabled: enabled && open,
    staleTime: 1000 * 60 * 60 * 24,
  });

  return (
    <Card className="gap-0 border-border bg-muted/40 py-0">
      <CardHeader className="p-0">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left"
        >
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="size-5 text-primary" />
            Recomendaciones para vos
          </CardTitle>
          <ChevronDown
            className={`size-5 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </CardHeader>
      {open && (
        <CardContent className="space-y-2.5 px-6 pb-5">
          {isLoading && (
            <>
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </>
          )}

          {!isLoading && data && !data.hasData && (
            <p className="text-sm text-muted-foreground">{data.summary}</p>
          )}

          {!isLoading && data?.hasData && (
            <>
              <p className="text-sm text-muted-foreground">{data.summary}</p>
              <ul className="space-y-2.5">
                {data.insights.map((ins, i) => (
                  <li key={i} className="flex items-start gap-3 rounded-md border border-border bg-card p-3">
                    <span className="mt-0.5 shrink-0">{severityIcon(ins.severity)}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">{ins.title}</div>
                      <div className="text-sm text-muted-foreground">{ins.body}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">{data.disclaimer}</p>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}
